import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, GripVertical, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { menusApi, type MenuItem, type StoreMenus } from '../../api/endpoints/menus';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Toggle } from '../../components/ui/Toggle';
import { Tabs } from '../../components/ui/Tabs';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Alert';

type MenuKey = 'header' | 'footer' | 'userMenu';

const ITEM_TYPES = [
  { value: 'custom', labelKey: 'menus.custom' },
  { value: 'all-categories', labelKey: 'menus.allCategories' },
  { value: 'all-categories-with-children', labelKey: 'menus.allCategoriesWithChildren' },
  { value: 'category', labelKey: 'menus.category' },
  { value: 'user-page', labelKey: 'menus.userPage' },
] as const;

function emptyItem(): MenuItem {
  return { title: '', link: '', type: 'custom' };
}

export function MenuManagement() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>('header');
  const [menus, setMenus] = useState<StoreMenus>({ header: [], footer: [], userMenu: [] });
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['menus', storeId],
    queryFn: () => menusApi.get(storeId!),
    enabled: !!storeId,
  });

  useEffect(() => {
    if (data?.menus) {
      setMenus(data.menus);
      setDirty(false);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => menusApi.save(storeId!, menus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menus', storeId] });
      toast('success', t('common.success'));
      setDirty(false);
    },
    onError: () => toast('error', t('common.error')),
  });

  const menuKey = activeTab as MenuKey;
  const items = menus[menuKey] ?? [];

  function updateItems(newItems: MenuItem[]) {
    setMenus(prev => ({ ...prev, [menuKey]: newItems }));
    setDirty(true);
  }

  function addItem() {
    updateItems([...items, emptyItem()]);
  }

  function updateItem(index: number, patch: Partial<MenuItem>) {
    const updated = [...items];
    updated[index] = { ...updated[index], ...patch };
    updateItems(updated);
  }

  function removeItem(index: number) {
    updateItems(items.filter((_, i) => i !== index));
  }

  function addChild(parentIndex: number) {
    const updated = [...items];
    const parent = { ...updated[parentIndex] };
    parent.children = [...(parent.children ?? []), emptyItem()];
    updated[parentIndex] = parent;
    updateItems(updated);
  }

  function updateChild(parentIndex: number, childIndex: number, patch: Partial<MenuItem>) {
    const updated = [...items];
    const parent = { ...updated[parentIndex] };
    const children = [...(parent.children ?? [])];
    children[childIndex] = { ...children[childIndex], ...patch };
    parent.children = children;
    updated[parentIndex] = parent;
    updateItems(updated);
  }

  function removeChild(parentIndex: number, childIndex: number) {
    const updated = [...items];
    const parent = { ...updated[parentIndex] };
    parent.children = (parent.children ?? []).filter((_, i) => i !== childIndex);
    updated[parentIndex] = parent;
    updateItems(updated);
  }

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('menus.title', 'Menu Management')}</h1>
        <Button onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending} disabled={!dirty}>
          {t('common.save')}
        </Button>
      </div>

      <Tabs
        tabs={[
          { key: 'header', label: t('menus.header', 'Header') },
          { key: 'footer', label: t('menus.footer', 'Footer') },
          { key: 'userMenu', label: t('menus.userMenu', 'User Menu') },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        className="mb-6"
      />

      {items.length === 0 ? (
        <EmptyState title={t('menus.empty', 'No menu items')} description={t('menus.emptyDesc', 'Add items to this menu.')} />
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="rounded-xl border border-border-default bg-bg-secondary p-4">
              <div className="flex items-start gap-3">
                <GripVertical className="h-5 w-5 text-text-muted mt-2 shrink-0 cursor-grab" />
                <div className="flex-1 space-y-3">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Input
                      label={t('common.name')}
                      value={item.title}
                      onChange={(e) => updateItem(i, { title: e.target.value })}
                    />
                    <Input
                      label={t('menus.link', 'Link')}
                      value={item.link}
                      onChange={(e) => updateItem(i, { link: e.target.value })}
                      dir="ltr"
                    />
                    <Select
                      label={t('common.type')}
                      options={ITEM_TYPES.map(t_ => ({ value: t_.value, label: t(t_.labelKey, t_.value) }))}
                      value={item.type}
                      onChange={(e) => updateItem(i, { type: e.target.value as MenuItem['type'] })}
                    />
                  </div>
                  <Toggle
                    label={t('menus.openNewTab', 'Open in new tab')}
                    checked={item.openInNewTab ?? false}
                    onChange={(v) => updateItem(i, { openInNewTab: v })}
                  />

                  {/* Children */}
                  {(item.children ?? []).length > 0 && (
                    <div className="ms-6 space-y-2 border-s-2 border-border-default ps-4">
                      {(item.children ?? []).map((child, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <Input
                            value={child.title}
                            onChange={(e) => updateChild(i, ci, { title: e.target.value })}
                            placeholder={t('common.name')}
                            className="flex-1"
                          />
                          <Input
                            value={child.link}
                            onChange={(e) => updateChild(i, ci, { link: e.target.value })}
                            placeholder={t('menus.link', 'Link')}
                            dir="ltr"
                            className="flex-1"
                          />
                          <button onClick={() => removeChild(i, ci)} className="text-text-muted hover:text-danger p-1">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => addChild(i)} className="text-xs text-accent hover:underline">
                    + {t('menus.addChild', 'Add Sub-item')}
                  </button>
                </div>
                <button onClick={() => removeItem(i)} className="text-text-muted hover:text-danger p-1.5 shrink-0">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={addItem}>
          <Plus className="h-4 w-4" />
          {t('menus.addItem', 'Add Menu Item')}
        </Button>

        {/* User menu presets */}
        {menuKey === 'userMenu' && (
          <>
            <span className="text-xs text-text-muted mx-1">|</span>
            {[
              { title: t('menus.presetAccount', 'My Account'), link: '/my-account' },
              { title: t('menus.presetOrders', 'My Orders'), link: '/orders' },
              { title: t('menus.presetWishlist', 'Wishlist'), link: '/wishlist' },
            ].map((preset) => (
              <Button
                key={preset.link}
                variant="ghost"
                size="sm"
                onClick={() => updateItems([...items, { ...emptyItem(), title: preset.title, link: preset.link, type: 'user-page' }])}
              >
                + {preset.title}
              </Button>
            ))}
          </>
        )}

        {/* Auto categories for header/footer */}
        {(menuKey === 'header' || menuKey === 'footer') && (
          <>
            <span className="text-xs text-text-muted mx-1">|</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateItems([...items, { ...emptyItem(), title: t('menus.allCategories', 'All Categories'), link: '', type: 'all-categories' }])}
            >
              + {t('menus.allCategories', 'All Categories')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateItems([...items, { ...emptyItem(), title: t('menus.allCategoriesWithChildren', 'Categories + Subs'), link: '', type: 'all-categories-with-children' }])}
            >
              + {t('menus.withSubs', 'With Subs')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
